import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import '@fontsource/caveat/500.css'
import '@fontsource/caveat/700.css'
import '@fontsource-variable/fraunces'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import { getAgenda, type AgendaItem } from '../../api/agenda'
import { getMySprints, type MySprint, type MyStory } from '../../api/me'
import { listWorkflowStates, updateStory, type StoryStatus } from '../../api/stories'
import { cn } from '../../design'

/**
 * My Work → Focus mode, drawn as an open paper planner (design:
 * claude.ai/artifact/7h5ufc4FrrGNdbMDoNo12H). Left page: the day — date,
 * mini month, today's schedule written into ruled hour lines. Right page:
 * what to do — the Next up index card, today's stories as sticky notes that
 * can be marked done, and the sprints in play pinned with their stats.
 *
 * Paper colours are fixed on purpose (it's a notebook, not a themed
 * surface), so this view ignores dark mode.
 */

const INK = '#1f2a44'
const PENCIL = '#b4432e'
const PAPER = '#f6f0e1'
const HAND = "'Caveat', cursive"
const SERIF = "'Fraunces Variable', Georgia, serif"
const MONO = "'IBM Plex Mono', ui-monospace, monospace"
const ROW = 44 // px per hour on the planner page
const LINE = 22 // one handwritten line
const STICKY_TINTS = ['#f7e27a', '#f5b7c5', '#bfe3b4', '#b9d8f2']
const SPRINT_TINTS = ['#f5b7c5', '#b9d8f2', '#bfe3b4', '#f7e27a']
const PRIORITY_RANK = { high: 0, med: 1, low: 2 } as const

export function FocusPlanner({
  stories,
  loading,
  blocked,
  now,
  onExit,
}: {
  /** Open stories due today or overdue (the Focus filter). */
  stories: MyStory[]
  loading: boolean
  blocked: number
  /** From useNow(): re-renders the page as time passes. */
  now: Date
  onExit: () => void
}) {
  const today = ymd(now)

  const agendaQ = useQuery({
    queryKey: ['agenda', today, today, 'my-work'],
    queryFn: () => getAgenda({ from: today, to: today, include_google: true }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
  const sprintsQ = useQuery({
    queryKey: ['me', 'sprints'],
    queryFn: getMySprints,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })

  const sorted = useMemo(() => [...stories].sort((a, b) =>
    (PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority])
    || (a.due_date ?? '').localeCompare(b.due_date ?? ''),
  ), [stories])
  const nextUp = sorted[0] ?? null
  const rest = sorted.slice(1)
  const overdue = stories.filter(s => s.due_date && s.due_date < today).length
  const sprints = sprintsQ.data ?? []

  return (
    <div className="min-h-full px-4 pb-16 pt-6 sm:px-8 lg:px-12" style={{ background: '#2f3b3a' }}>
      <div className="mx-auto max-w-[1400px] space-y-4">
        {/* desk toolbar */}
        <div className="flex items-center justify-between gap-3" style={{ color: '#e9e2d0' }}>
          <div className="flex items-center gap-3">
            <span style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.14em', color: '#b9c4bf' }} className="uppercase">My Work</span>
            <span className="h-1 w-1 rounded-full" style={{ background: '#7d8a86' }} />
            <span style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 600 }}>Focus</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/calendar"
              className="inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm no-underline hover:bg-white/5"
              style={{ borderColor: '#56635f', color: '#e9e2d0' }}
            >
              <CalendarGlyph /> Full calendar
            </Link>
            <button
              type="button"
              onClick={onExit}
              className="inline-flex h-11 items-center gap-2 rounded-full px-4 text-sm font-medium hover:brightness-95"
              style={{ background: '#e9e2d0', color: INK }}
            >
              <XGlyph /> Close the planner
            </button>
          </div>
        </div>

        {/* notebook */}
        <div className="relative grid rounded-md lg:grid-cols-2" style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.35), 0 2px 0 #d8cfb8', color: INK }}>
          {/* ribbon bookmark */}
          <div
            aria-hidden
            className="absolute z-10 hidden lg:block"
            style={{ bottom: -44, left: 'calc(50% - 64px)', width: 30, height: 90, background: PENCIL, clipPath: 'polygon(0 0,100% 0,100% 100%,50% 84%,0 100%)' }}
          />
          {/* spiral binding */}
          <div aria-hidden className="absolute bottom-4 top-4 z-10 hidden flex-col justify-between lg:flex" style={{ left: 'calc(50% - 11px)' }}>
            {Array.from({ length: 18 }, (_, i) => (
              <span key={i} className="block box-border" style={{ width: 22, height: 10, borderRadius: 6, border: '2px solid #8f949a', background: '#2f3b3a' }} />
            ))}
          </div>

          <DayPage now={now} items={agendaQ.data?.items ?? []} loading={agendaQ.isLoading} />

          {/* RIGHT PAGE */}
          <section
            aria-label="What to do"
            className="relative flex flex-col gap-5 rounded-b-md px-6 pb-6 pt-8 sm:px-10 lg:rounded-l-none lg:rounded-r-md lg:pl-12"
            style={{ background: PAPER }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: 0 }}>On deck</h2>
              <span style={{ fontFamily: HAND, fontSize: 22, color: '#4a4a4a' }}>
                {loading ? '…' : `${stories.length} left · ${overdue} overdue · ${blocked} blocked · ${sprints.length} ${sprints.length === 1 ? 'sprint' : 'sprints'}`}
              </span>
            </div>

            {nextUp ? <NextUpCard story={nextUp} today={today} /> : !loading && (
              <p style={{ fontFamily: HAND, fontSize: 26, color: '#7a8aa0', margin: '8px 0' }}>
                Nothing due today or overdue — clear runway.
              </p>
            )}

            {rest.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <Label>Due today — peel off when done</Label>
                <div className="grid grid-cols-1 gap-[18px] pt-1.5 sm:grid-cols-2 xl:grid-cols-3">
                  {rest.slice(0, 6).map((s, i) => (
                    <StickyNote key={s.id} story={s} index={i} today={today} agenda={agendaQ.data?.items ?? []} />
                  ))}
                </div>
                {rest.length > 6 && (
                  <span style={{ fontFamily: HAND, fontSize: 20, color: '#7a8aa0' }}>+ {rest.length - 6} more in the list view</span>
                )}
              </div>
            )}

            <SprintsSection sprints={sprints} loading={sprintsQ.isLoading} />

            <span className="mt-auto text-right" style={{ fontFamily: MONO, fontSize: 10, color: '#a0957c' }}>— 2 —</span>
          </section>
        </div>
      </div>
    </div>
  )
}

// ─── Left page: the day ───────────────────────────────────────────────────

function DayPage({ now, items, loading }: { now: Date; items: AgendaItem[]; loading: boolean }) {
  const navigate = useNavigate()
  const dueToday = items.filter(it => it.kind === 'story_due')
  const allDay = items.filter(it => it.all_day && it.kind !== 'story_due')
  const timed = items.filter(it => !it.all_day)

  // Hours shown: the working day, stretched to whatever is on it.
  const hours = timed.map(it => parseDt(it.start_at).getHours())
  const ends = timed.map(it => (it.end_at ? parseDt(it.end_at) : parseDt(it.start_at)).getHours() + 1)
  const first = Math.min(9, ...hours)
  const last = Math.max(18, ...ends)
  const span = last - first

  // Written one line per item; items too close to the previous one drop to the next free line.
  const lines = layoutLines(timed, first, now)
  const floor = lines.length ? Math.max(...lines.map(l => l.top + l.height)) : 0
  const nowTop = ((now.getHours() - first) * 60 + now.getMinutes()) / 60 * ROW
  const lastEnd = lines.length ? Math.max(...lines.map(l => l.end.getTime())) : null
  const freeFrom = lastEnd !== null && new Date(lastEnd).getHours() < 17 ? new Date(lastEnd) : null

  function open(it: AgendaItem) {
    if (it.story) navigate(`/projects/${it.story.project_key}/backlog?story=${it.story.key}`)
    else if (it.event) navigate(`/calendar?date=${ymd(now)}&event=${it.event.id}`)
    else if (it.google?.html_link) window.open(it.google.html_link, '_blank', 'noreferrer')
  }

  return (
    <section
      aria-label="Today"
      className="relative flex flex-col gap-4 rounded-t-md px-6 pb-6 pt-8 sm:pl-16 sm:pr-10 lg:rounded-r-none lg:rounded-l-md"
      style={{ background: PAPER, borderRight: '1px solid #ddd3bb' }}
    >
      <div aria-hidden className="absolute bottom-0 top-0 hidden sm:block" style={{ left: 48, width: 1, background: '#e2a698' }} />

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex flex-col gap-1">
          <Label>Week {isoWeek(now)} · Day {dayOfYear(now)}</Label>
          <span style={{ fontFamily: SERIF, fontSize: 56, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {now.toLocaleDateString(undefined, { weekday: 'long' })}
          </span>
          <span style={{ fontFamily: SERIF, fontSize: 20, color: '#4a4a4a' }}>
            {now.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        <MiniMonth now={now} />
      </div>

      {(dueToday.length > 0 || allDay.length > 0) && (
        <div className="flex flex-col gap-1 rounded px-3 py-2" style={{ border: '1px dashed #c9b98f', background: '#fbf5dc' }}>
          {dueToday.length > 0 && (
            <div className="flex flex-wrap items-center gap-2.5">
              <Label>Due today</Label>
              <span style={{ fontFamily: HAND, fontSize: 22 }}>
                {dueToday.map(it => it.story?.key).filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
          {allDay.map(it => (
            <button key={it.id} type="button" onClick={() => open(it)} className="text-left" style={{ fontFamily: HAND, fontSize: 20 }}>
              <Label>All day</Label> {it.title}
            </button>
          ))}
        </div>
      )}

      {/* hour planner */}
      <div className="flex gap-3">
        <div className="flex w-11 shrink-0 flex-col" style={{ fontFamily: MONO, fontSize: 11, color: '#8a7f68' }}>
          {Array.from({ length: span }, (_, i) => (
            <span key={i} style={{ height: ROW, lineHeight: `${ROW}px` }}>{fmtHour(first + i)}</span>
          ))}
        </div>
        <div
          className="relative flex-grow"
          style={{
            height: span * ROW,
            backgroundImage: `repeating-linear-gradient(to bottom,transparent 0,transparent ${ROW - 1}px,#c9d6e3 ${ROW - 1}px,#c9d6e3 ${ROW}px)`,
          }}
        >
          {loading && <span className="absolute left-2 top-2" style={{ fontFamily: HAND, fontSize: 20, color: '#7a8aa0' }}>opening today's page…</span>}
          {!loading && lines.length === 0 && (
            <span className="absolute left-3" style={{ top: ROW * 1.5, fontFamily: HAND, fontSize: 22, color: '#7a8aa0' }}>
              — nothing on the calendar today —
            </span>
          )}
          {lines.map(({ it, start, end, top, height, past, current }) => {
            const color = it.project?.color ?? it.color ?? (it.kind === 'google' ? '#4285f4' : '#e8c457')
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => open(it)}
                title={it.title}
                className="absolute left-0 flex items-center gap-2.5 rounded-sm pl-1.5 text-left hover:bg-black/5"
                style={{ top, height, right: 60, alignItems: 'flex-start', background: current ? 'rgba(232,196,87,0.35)' : undefined }}
              >
                <span className="shrink-0 rounded-[1px]" style={{ width: 6, height: Math.max(18, height - 4), marginTop: 2, background: color, opacity: past ? 0.5 : 1 }} />
                <span
                  className="truncate"
                  style={{
                    fontFamily: HAND,
                    fontSize: 22,
                    lineHeight: `${LINE}px`,
                    fontWeight: current ? 700 : 500,
                    color: past ? '#8b8676' : INK,
                    textDecoration: past ? 'line-through' : undefined,
                    textDecorationColor: PENCIL,
                  }}
                >
                  {fmtClock(start)}{height > LINE + 4 ? `–${fmtClock(end)}` : ''} {it.title}
                </span>
              </button>
            )
          })}
          {nowTop >= 0 && nowTop <= span * ROW && (
            <div aria-hidden className="pointer-events-none absolute right-0 flex items-center gap-1" style={{ top: nowTop - 11, width: 56 }}>
              <svg width="18" height="10" viewBox="0 0 20 10" fill="none" stroke={PENCIL} strokeWidth="1.6" strokeLinecap="round"><path d="M19 5H2M6 1L2 5l4 4" /></svg>
              <span style={{ fontFamily: HAND, fontSize: 18, color: PENCIL, lineHeight: '22px' }}>now</span>
            </div>
          )}
          {freeFrom && (
            <span
              className="absolute left-3"
              style={{ top: Math.max(floor + 20, ((freeFrom.getHours() - first) * 60 + freeFrom.getMinutes()) / 60 * ROW + 20), fontFamily: HAND, fontSize: 20, color: '#7a8aa0' }}
            >
              — open from {fmtClock(freeFrom)} on: it's yours —
            </span>
          )}
        </div>
      </div>

      <span className="mt-auto text-center" style={{ fontFamily: MONO, fontSize: 10, color: '#a0957c' }}>— 1 —</span>
    </section>
  )
}

/**
 * Planner entries at their real length: a block spans start→end on the hour
 * lines (never shorter than one written line). An entry that would start
 * inside the previous one's first line drops below it so the writing never
 * collides; longer overlaps simply share the band.
 */
function layoutLines(timed: AgendaItem[], first: number, now: Date) {
  const out: Array<{ it: AgendaItem; start: Date; end: Date; top: number; height: number; past: boolean; current: boolean }> = []
  for (const it of [...timed].sort((a, b) => a.start_at.localeCompare(b.start_at))) {
    const start = parseDt(it.start_at)
    const end = it.end_at ? parseDt(it.end_at) : new Date(start.getTime() + 30 * 60_000)
    const prev = out[out.length - 1]
    const naturalTop = ((start.getHours() - first) * 60 + start.getMinutes()) / 60 * ROW
    const top = Math.max(naturalTop, prev ? prev.top + LINE : -Infinity)
    const height = Math.max(LINE, ((end.getTime() - start.getTime()) / 3_600_000) * ROW - (top - naturalTop))
    out.push({ it, start, end, top, height, past: end <= now, current: start <= now && end > now })
  }
  return out
}

function MiniMonth({ now }: { now: Date }) {
  const y = now.getFullYear()
  const m = now.getMonth()
  const lead = new Date(y, m, 1).getDay()
  const days = new Date(y, m + 1, 0).getDate()
  const cells: Array<number | null> = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)]
  return (
    <div className="flex w-[196px] flex-col gap-1.5 rounded px-3 py-2.5" style={{ border: '1px solid #d8cdb2', background: '#fbf7ec' }}>
      <span className="text-center" style={{ fontFamily: SERIF, fontSize: 13, fontWeight: 600 }}>
        {now.toLocaleDateString(undefined, { month: 'long' })}
      </span>
      <div className="grid grid-cols-7 gap-0.5 text-center" style={{ fontFamily: MONO, fontSize: 10, color: '#6b604b' }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={'h' + i}>{d}</span>)}
        {cells.map((d, i) => (
          <span
            key={i}
            style={d === now.getDate() ? { color: '#fff', background: PENCIL, borderRadius: 9, fontWeight: 500 } : undefined}
          >
            {d ?? ''}
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── Right page pieces ────────────────────────────────────────────────────

/** Moves a story to its project's first state in `category`. */
function useMoveStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ story, category }: { story: MyStory; category: StoryStatus }) => {
      const states = await qc.fetchQuery({
        queryKey: ['workflow-states', story.project_id],
        queryFn: () => listWorkflowStates(story.project_id),
        staleTime: 60_000,
      })
      const target = [...states].filter(s => s.category === category).sort((a, b) => a.sort_order - b.sort_order)[0]
      if (!target) throw new Error(`${story.project_key} has no "${category.replace('_', ' ')}" state`)
      return updateStory(story.id, { status_id: target.id })
    },
    onSuccess: () => {
      for (const key of [['me'], ['stories'], ['agenda'], ['dashboard']]) qc.invalidateQueries({ queryKey: key })
    },
  })
}

function NextUpCard({ story: s, today }: { story: MyStory; today: string }) {
  const navigate = useNavigate()
  const move = useMoveStory()
  const overdue = !!s.due_date && s.due_date < today
  const openStory = () => navigate(`/projects/${s.project_key}/backlog?story=${s.key}`)

  return (
    <div className="relative ml-1 mr-2 mt-1.5" style={{ transform: 'rotate(-1.2deg)' }}>
      <div aria-hidden className="absolute z-[2]" style={{ top: -12, left: '46%', width: 92, height: 24, background: 'rgba(233,224,196,0.85)', transform: 'rotate(3deg)', boxShadow: '0 1px 1px rgba(0,0,0,0.08)' }} />
      <div
        className="flex flex-col gap-2 px-6 pb-4 pt-4"
        style={{
          background: '#fff',
          border: '1px solid #e1d8c2',
          boxShadow: '0 6px 14px rgba(60,40,10,0.14)',
          backgroundImage: 'repeating-linear-gradient(to bottom,transparent 0,transparent 31px,#dfe8f1 31px,#dfe8f1 32px)',
          backgroundPosition: '0 44px',
        }}
      >
        <div aria-hidden style={{ height: 2, background: '#e2a698', margin: '0 -24px 6px' }} />
        <div className="flex flex-wrap items-center gap-2.5">
          <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: '0.14em', color: PENCIL }} className="uppercase">Next up</span>
          <span style={{ fontFamily: MONO, fontSize: 12, color: '#6b604b' }}>{s.key}</span>
          {s.due_date && (
            <Stamp className="ml-auto">{overdue ? `Overdue · ${fmtShort(s.due_date)}` : 'Due today'}</Stamp>
          )}
        </div>
        <button type="button" onClick={openStory} className="text-left" style={{ fontFamily: HAND, fontSize: 32, fontWeight: 700, lineHeight: 1.05, color: INK }}>
          {s.title}
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[13px]" style={{ color: '#4a4a4a' }}>
            {s.project_name} · {s.priority} priority · {s.status_name}
          </span>
          <button
            type="button"
            disabled={move.isPending}
            onClick={() => {
              if (s.status_category === 'todo') move.mutate({ story: s, category: 'in_progress' }, { onSettled: openStory })
              else openStory()
            }}
            className="ml-auto inline-flex h-11 items-center rounded px-[18px] text-sm font-medium hover:brightness-110 disabled:opacity-60"
            style={{ background: INK, color: PAPER }}
          >
            {s.status_category === 'todo' ? 'Start on it' : 'Pick it back up'}
          </button>
        </div>
        {move.error && <span className="text-xs" style={{ color: PENCIL }}>{(move.error as Error).message}</span>}
      </div>
    </div>
  )
}

function StickyNote({ story: s, index, today, agenda }: { story: MyStory; index: number; today: string; agenda: AgendaItem[] }) {
  const navigate = useNavigate()
  const move = useMoveStory()
  const [done, setDone] = useState(false)
  const tilt = [-2, 1.5, -0.8, 1.1, -1.4, 0.7][index % 6]
  const tint = STICKY_TINTS[s.project_id % STICKY_TINTS.length]
  const slot = agenda.find(it => it.kind === 'story_scheduled' && it.story?.id === s.id)
  const when = slot ? fmtClock(parseDt(slot.start_at)) : s.due_date && s.due_date < today ? `overdue ${fmtShort(s.due_date)}` : 'today'

  return (
    <div
      className="relative box-border flex min-h-[150px] flex-col gap-1.5 px-3.5 pb-1 pt-4 transition-all"
      style={{
        background: tint,
        boxShadow: '0 8px 14px rgba(60,40,10,0.18)',
        transform: `rotate(${tilt}deg)${done ? ' translateY(-6px)' : ''}`,
        opacity: done ? 0.55 : 1,
      }}
    >
      <div aria-hidden className="absolute left-0 right-0 top-0 h-4" style={{ background: 'rgba(0,0,0,0.05)' }} />
      <span className="pt-1" style={{ fontFamily: MONO, fontSize: 11, color: '#6b5a1f' }}>{s.key} · {when}</span>
      <button
        type="button"
        onClick={() => navigate(`/projects/${s.project_key}/backlog?story=${s.key}`)}
        className="text-left"
        style={{ fontFamily: HAND, fontSize: 22, lineHeight: 1.05, color: '#2a2412', textDecoration: done ? 'line-through' : undefined, textDecorationColor: PENCIL }}
      >
        {s.title}
      </button>
      <button
        type="button"
        aria-pressed={done}
        disabled={done || move.isPending}
        onClick={() => move.mutate({ story: s, category: 'done' }, { onSuccess: () => setDone(true) })}
        className="mt-auto inline-flex min-h-11 items-center gap-2 text-left text-[13px] disabled:cursor-default"
        style={{ color: '#3d3310' }}
      >
        <span className="box-border inline-flex h-5 w-5 items-center justify-center rounded-[3px]" style={{ border: '1.5px solid #3d3310', background: 'rgba(255,255,255,0.35)' }}>
          {done && <CheckGlyph color={PENCIL} />}
        </span>
        {done ? 'Done — nice' : move.isPending ? 'Marking…' : 'Mark done'}
      </button>
      {move.error && <span className="text-[11px]" style={{ color: PENCIL }}>{(move.error as Error).message}</span>}
    </div>
  )
}

function SprintsSection({ sprints, loading }: { sprints: MySprint[]; loading: boolean }) {
  const [pickedId, setPickedId] = useState<number | null>(null)
  const picked = sprints.find(s => s.id === pickedId) ?? sprints[0] ?? null

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between">
        <Label>Sprints — pinned</Label>
        {picked && (
          <Link to={`/projects/${picked.project.key}/sprints`} className="text-[13px]" style={{ color: PENCIL }}>All sprints</Link>
        )}
      </div>
      {loading && <span style={{ fontFamily: HAND, fontSize: 20, color: '#7a8aa0' }}>pinning sprints…</span>}
      {!loading && sprints.length === 0 && (
        <div className="flex min-h-[120px] w-[236px] items-center justify-center px-4 text-center" style={{ border: '2px dashed #c9b98f', fontFamily: HAND, fontSize: 22, color: '#7a8aa0' }}>
          No sprint in play right now.
        </div>
      )}
      {picked && (
        <div className="flex flex-col gap-5 md:flex-row md:items-stretch">
          <div className="flex gap-5 overflow-x-auto pb-2 pt-3 md:flex-col md:overflow-visible md:pb-0">
            {sprints.map((sp, i) => (
              <SprintNote key={sp.id} sprint={sp} tint={SPRINT_TINTS[i % SPRINT_TINTS.length]} tilt={i % 2 ? -1.2 : 1.4} />
            ))}
          </div>
          <SprintStats sprint={picked} sprints={sprints} onPick={setPickedId} />
        </div>
      )}
    </div>
  )
}

/** A pinned sprint note; clicking it opens that sprint in the project's Sprint Planning. */
function SprintNote({ sprint: sp, tint, tilt }: { sprint: MySprint; tint: string; tilt: number }) {
  const boxes = sp.story_count <= 10
  const nextOpen = sp.open_stories[0]
  return (
    <Link
      to={`/projects/${sp.project.key}/sprints?sprint=${sp.id}`}
      title={`Open ${sp.name} in ${sp.project.key} Sprint Planning`}
      className="relative box-border flex w-[236px] shrink-0 flex-col gap-2 px-4 pb-3.5 pt-5 text-left no-underline transition-transform hover:-translate-y-0.5"
      style={{
        background: tint,
        boxShadow: '0 8px 14px rgba(60,40,10,0.18)',
        transform: `rotate(${tilt}deg)`,
        color: 'inherit',
      }}
    >
      <span aria-hidden className="absolute" style={{ top: -9, left: 'calc(50% - 9px)', width: 18, height: 18, borderRadius: 9, background: PENCIL, boxShadow: '0 2px 2px rgba(0,0,0,0.3), inset -3px -3px 0 rgba(0,0,0,0.15)' }} />
      <span className="flex items-center justify-between gap-2">
        <span className="truncate" style={{ fontFamily: MONO, fontSize: 11, color: '#5c2a36' }}>{sp.project.key} · {sp.project.name}</span>
        <Stamp>{daysLabel(sp.days_left)}</Stamp>
      </span>
      <span style={{ fontFamily: HAND, fontSize: 30, fontWeight: 700, lineHeight: 1, color: '#2a1218' }}>{sp.name}</span>
      <span style={{ fontFamily: MONO, fontSize: 11, color: '#5c2a36' }}>
        {sp.start_date ? fmtShort(sp.start_date) : '—'} → {sp.end_date ? fmtShort(sp.end_date) : 'no end date'}
      </span>
      {boxes ? (
        <span className="flex flex-wrap gap-1.5 pt-0.5" aria-label={`${sp.done_count} of ${sp.story_count} stories done`}>
          {Array.from({ length: sp.story_count }, (_, i) => (
            <span
              key={i}
              className="box-border inline-flex h-6 w-6 items-center justify-center rounded-[3px]"
              style={{ border: '1.5px solid #2a1218', background: i < sp.done_count ? 'rgba(255,255,255,0.45)' : 'transparent' }}
            >
              {i < sp.done_count && <CheckGlyph color="#2a1218" />}
            </span>
          ))}
        </span>
      ) : (
        <span className="block h-3 w-full rounded-sm" style={{ border: '1.5px solid #2a1218' }} aria-label={`${sp.percent_done}% done`}>
          <span className="block h-full" style={{ width: `${sp.percent_done}%`, background: 'repeating-linear-gradient(45deg,#2a1218 0,#2a1218 1.5px,transparent 1.5px,transparent 5px)' }} />
        </span>
      )}
      {nextOpen && (
        <span className="truncate" style={{ fontFamily: HAND, fontSize: 20, color: '#2a1218', lineHeight: 1.05 }}>
          {sp.open_stories.length === 1 ? 'Last one' : 'Next'}: {nextOpen.key} {nextOpen.title}
        </span>
      )}
      <span className="self-end" style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', color: '#5c2a36' }}>OPEN SPRINT →</span>
    </Link>
  )
}

function SprintStats({ sprint: sp, sprints, onPick }: { sprint: MySprint; sprints: MySprint[]; onPick: (id: number) => void }) {
  const over = sp.days_left !== null && sp.days_left < 0
  const nudge =
    sp.status === 'planned' ? `Still marked “planned” — mark it active or close it${sp.open_stories.length ? ` and roll ${sp.open_stories.map(o => o.key).join(', ')} forward` : ''}.`
    : over && sp.open_stories.length ? `Past its end date with ${sp.open_stories.length} open — close it or roll them over.`
    : sp.total_points === 0 && sp.story_count > 0 ? 'Nothing estimated — add points to track velocity.'
    : null

  return (
    <div
      className="box-border flex flex-grow flex-col gap-2.5 px-4 py-3.5"
      style={{
        background: '#fbfaf4',
        border: '1px solid #d6dccf',
        boxShadow: '0 4px 10px rgba(60,40,10,0.10)',
        backgroundImage: 'linear-gradient(#e3e9e0 1px, transparent 1px), linear-gradient(90deg, #e3e9e0 1px, transparent 1px)',
        backgroundSize: '16px 16px',
      }}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 600 }}>Sprint stats{sprints.length === 1 ? ` · ${sp.name}` : ''}</span>
        {sprints.length > 1 && sprints.map(o => (
          <button
            key={o.id}
            type="button"
            onClick={() => onPick(o.id)}
            aria-pressed={o.id === sp.id}
            className="min-h-8 text-[13px]"
            style={{ fontFamily: HAND, fontSize: 19, color: o.id === sp.id ? INK : '#7a8aa0', textDecoration: o.id === sp.id ? 'underline' : 'none', textDecorationColor: PENCIL }}
          >
            {o.name}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-3.5 gap-y-2.5">
        <Stat value={`${sp.done_count}/${sp.story_count}`} label={`stories done · ${sp.percent_done}%`} />
        <Stat
          value={sp.days_left === null ? '—' : over ? `−${Math.abs(sp.days_left)}` : String(sp.days_left)}
          label={sp.days_left === null ? 'no end date' : over ? 'days past end date' : 'days left'}
          color={over ? PENCIL : INK}
        />
        <Stat
          value={String(sp.in_progress_count)}
          label={`in progress${sp.in_progress_count && sp.open_stories[0] ? ` (${sp.open_stories.filter(o => o.status_category !== 'todo').map(o => o.key).join(', ')})` : ''}`}
        />
        <Stat
          value={sp.total_points ? `${sp.done_points}/${sp.total_points}` : '0 pts'}
          label={sp.total_points ? 'points done' : 'nothing estimated yet'}
          color={sp.total_points ? INK : '#7a8aa0'}
        />
        <Stat value={`${sp.my_story_count}`} label={`of ${sp.story_count} are yours`} />
      </div>
      {nudge && <span className="mt-auto" style={{ fontFamily: HAND, fontSize: 19, color: '#7a8aa0', lineHeight: 1.1 }}>{nudge}</span>}
    </div>
  )
}

function Stat({ value, label, color = INK }: { value: string; label: string; color?: string }) {
  return (
    <div className="flex flex-col">
      <span style={{ fontFamily: HAND, fontSize: 34, fontWeight: 700, lineHeight: 1, color }}>{value}</span>
      <span className="text-xs" style={{ color: '#4a4a4a' }}>{label}</span>
    </div>
  )
}

// ─── bits ─────────────────────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return <span className="uppercase" style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.14em', color: '#7a6f58' }}>{children}</span>
}

function Stamp({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn('shrink-0 whitespace-nowrap uppercase', className)}
      style={{ fontFamily: MONO, fontSize: 10, padding: '2px 6px', border: `1.5px solid ${PENCIL}`, color: PENCIL, borderRadius: 3, transform: 'rotate(-5deg)' }}
    >
      {children}
    </span>
  )
}

function CheckGlyph({ color }: { color: string }) {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M4 13l5 5L20 6" /></svg>
}
function CalendarGlyph() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
}
function XGlyph() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
}

function daysLabel(d: number | null): string {
  if (d === null) return 'open-ended'
  if (d < 0) return `${Math.abs(d)} ${Math.abs(d) === 1 ? 'day' : 'days'} over`
  if (d === 0) return 'ends today'
  return `${d} ${d === 1 ? 'day' : 'days'} left`
}
function parseDt(s: string): Date { return new Date(s.replace(' ', 'T')) }
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtShort(iso: string): string {
  return new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
function fmtHour(h: number): string {
  return `${h % 12 === 0 ? 12 : h % 12}${h >= 12 ? 'p' : 'a'}`
}
function fmtClock(d: Date): string {
  const h = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12
  return d.getMinutes() ? `${h}:${String(d.getMinutes()).padStart(2, '0')}` : `${h}:00`
}
function dayOfYear(d: Date): number {
  return Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(d.getFullYear(), 0, 1).getTime()) / 86_400_000) + 1
}
function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day = t.getUTCDay() || 7
  t.setUTCDate(t.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)
}
